<!-- src/components/AppLoader.vue -->
<script setup>
import { ref, onMounted, onUnmounted, watch, defineProps } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  text: {
    type: String,
    default: 'Trwa ładowanie...'
  }
})

// Pula znaków
const charPool = '0123456789ABCDEFGHJKLMNPQRSTUVWXYZ'

// Generowanie obiektów znaków z flagą koloru czerwonego
const generateRowObjects = (length = 30) => {
  const row = []
  for (let i = 0; i < length; i++) {
    row.push({
      char: charPool[Math.floor(Math.random() * charPool.length)],
      // ~8% szans na to, że dany znak będzie czerwony
      isRed: Math.random() < 0.08
    })
  }
  return row
}

const columns = ref([
  generateRowObjects(30),
  generateRowObjects(30),
  generateRowObjects(30)
])

let timer = null

// Płynna zmiana znaków i losowa zmiana kolorów na czerwony
const updateChars = () => {
  columns.value = columns.value.map(row => {
    const updated = [...row]
    // Modyfikujemy 2-3 znaki w każdym wierszu
    for (let k = 0; k < 3; k++) {
      const idx = Math.floor(Math.random() * updated.length)
      updated[idx] = {
        char: charPool[Math.floor(Math.random() * charPool.length)],
        isRed: Math.random() < 0.08
      }
    }
    return updated
  })
}

const startAnimation = () => {
  if (timer) clearInterval(timer)
  timer = setInterval(updateChars, 60)
}

const stopAnimation = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

watch(() => props.show, (newVal) => {
  if (newVal) startAnimation()
  else stopAnimation()
})

onMounted(() => {
  if (props.show) startAnimation()
})

onUnmounted(() => {
  stopAnimation()
})
</script>

<template>
  <Transition name="fade">
    <div v-if="show" class="loader-overlay">

      <!-- Pudełko loader-card z animowaną krążącą ramką -->
      <div class="loader-card-border">
        <div class="loader-card text-center">

          <!-- Wskaźnik i napis -->
          <div class="d-flex align-items-center justify-content-center gap-2 mb-3">
            <div class="pulse-dot"></div>
            <span class="loader-title">{{ text }}</span>
          </div>

          <!-- 3 kolumny po 30 znaków z czerwonymi akcentami -->
          <div class="code-container">
            <div v-for="(col, colIdx) in columns" :key="colIdx" class="code-line">
              <span v-for="(item, charIdx) in col" :key="charIdx" :class="{ 'text-red': item.isRed }">
                {{ item.char }}
              </span>
            </div>
          </div>

        </div>
      </div>

    </div>
  </Transition>
</template>

<style scoped>
.loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(25, 25, 25, 0.5);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* KONTENER RAMKI Z KRĄŻĄCYM PASKIEM LIGHT (20% OBWODU) */
.loader-card-border {
  position: relative;
  border-radius: 14px;
  padding: 5px;
  /* Szerokość obramowania */
  background: #e2e8f0;
  overflow: hidden;
  max-width: 380px;
  width: 90%;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04);
}

/* Pasek krążący po krawędziach (gradient obracający się w tle pod ramką) */
.loader-card-border::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  /*background: conic-gradient(
    transparent 0deg,
    transparent 290deg,
    #0d6efd 330deg,
    #0d6efd 360deg
  );
  animation: rotateBorder 2.5s linear infinite;*/
}

@keyframes rotateBorder {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* WNĘTRZE KARTY */
.loader-card {
  position: relative;
  z-index: 1;
  background: #ffffff;
  padding: 24px 28px;
  border-radius: 12px;
  width: 100%;
}

.loader-title {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.95rem;
  letter-spacing: 0.3px;
}

/* Kropka z dyskretną pulsacją */
.pulse-dot {
  width: 8px;
  height: 8px;
  background-color: #0d6efd;
  border-radius: 50%;
  animation: pulse 1.2s ease-in-out infinite alternate;
}

@keyframes pulse {
  0% {
    transform: scale(0.8);
    opacity: 0.5;
  }

  100% {
    transform: scale(1.3);
    opacity: 1;
  }
}

/* Kontener na ciągi cyfr */
.code-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  padding: 12px 14px;
  border-radius: 8px;
}

.code-line {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.75rem;
  color: #0d6efd;
  letter-spacing: 1.2px;
  word-break: break-all;
  user-select: none;
  opacity: 0.85;
}

@media (max-width: 767px) {
  .code-line {

    font-size: 0.65rem;
  }
}

.code-line:nth-child(2) {
  opacity: 0.65;
}

/* KLASA CZERWONYCH ZNAKÓW */
.text-red {
  color: #dc3545 !important;
  font-weight: bold;
  text-shadow: 0 0 4px rgba(220, 53, 69, 0.4);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>